import {TextInput} from '@dropbox/dig-components/text_fields';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {SearchLine, WorkstreamsLine} from '@dropbox/dig-icons/assets';
import type {Employee, WorkstreamBasic} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {useHoverShade} from 'components/DSYS/useHoverShade';
import {GlobalSearchResults, isEmployee, isWorkstream, useGlobalSearch} from 'hooks/useEmployee';
import {useRef, useState} from 'react';

import {TOUCH_TARGET_PX} from './treeTypes';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

// `visits`: when it was opened (ms, oldest first), for recents' frecency order.
// `unpinnedAt`: when its pin was last removed (ms), so it holds its place a while.
export type PinnedItem = (
  | {type: 'person'; ldap: string; name: string; profilePhoto?: string | null}
  | {type: 'workstream'; id: number; name: string}
  | {type: 'view'; key: string; name: string}
) & {pinned?: boolean; visits?: number[]; unpinnedAt?: number};

// Up to this many workstream rows; people fill the rest.
const MAX_WORKSTREAM_ROWS = 5;
const MAX_ROWS = 8;

type Result = NonNullable<GlobalSearchResults>;

// Typeahead.Row values: people and workstreams share one list, so prefix the id.
const rowValue = (result: Result) =>
  isEmployee(result) ? `p-${result.ldap}` : `w-${(result as WorkstreamBasic).id}`;

/**
 * /workstreams: the rail's "Find people or workstreams". The row
 * turns into a DIG Typeahead field on click (↑/↓ to move, Enter to pick, Esc
 * to put the row back); a pick opens it through `onPin`.
 */
export const WorkstreamsSearch = ({
  onPin,
  bleed,
  touch = false,
}: {
  onPin: (item: PinnedItem) => void;
  // Full-bleed rail (/workstreams): hover fill spans the rail edge to edge
  // like the tree rows (px the rail pads on each side).
  bleed?: {left: number; right: number};
  // Narrow screens' sheet: a finger-sized row.
  touch?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Only a full-bleed row (the rail's) tints.
  const shade = useHoverShade(Boolean(bleed) && hovered);
  const containerRef = useRef<HTMLElement | null>(null);
  const rowHeight = touch ? TOUCH_TARGET_PX : bleed ? 36 : 32;

  // Separate searches: one 'all' search fills its limit people-first, so a
  // query that matches enough titles ("docsend") never reaches workstreams.
  const {data: workstreamResults, isFetching: isFetchingWorkstreams} = useGlobalSearch({
    input: inputValue,
    filter: 'workstreams',
    limit: MAX_ROWS,
    includeWorkstreamScopes: true,
    preloadFallback: false,
  });
  const {data: peopleResults, isFetching: isFetchingPeople} = useGlobalSearch({
    input: inputValue,
    filter: 'people',
    limit: MAX_ROWS,
    preloadFallback: false,
  });

  const isResult = (r: GlobalSearchResults): r is Result => !!r && !('idx' in r);
  const filtered = [
    ...(workstreamResults ?? [])
      .filter((r) => isResult(r) && isWorkstream(r))
      .slice(0, MAX_WORKSTREAM_ROWS),
    ...(peopleResults ?? []).filter((r) => isResult(r) && isEmployee(r)),
  ]
    .filter(isResult)
    .slice(0, MAX_ROWS);

  const startEditing = () => {
    logWorkstreamsInteraction('search_opened');
    setIsEditing(true);
  };

  const stopEditing = () => {
    setInputValue('');
    setIsEditing(false);
    setHovered(false);
  };

  const handleSelection = (value: string) => {
    const result = filtered.find((r) => rowValue(r) === value);
    if (!result) return;
    if (isEmployee(result)) {
      onPin({
        type: 'person',
        ldap: result.ldap,
        name: result.name,
        profilePhoto: result.profile_photo_url,
      });
    } else if (isWorkstream(result)) {
      onPin({type: 'workstream', id: result.id, name: result.name});
    }
    stopEditing();
  };

  const renderRow = (result: Result) =>
    isEmployee(result) ? (
      <Typeahead.Row
        key={rowValue(result)}
        value={rowValue(result)}
        withLeftAccessory={<Avatar user={result as Employee} size="small" />}
        withTitle={<Text isBold>{result.name}</Text>}
        withSubtitle={(result as Employee).role}
      />
    ) : (
      <Typeahead.Row
        key={rowValue(result)}
        value={rowValue(result)}
        withLeftAccessory={<UIIcon src={WorkstreamsLine} />}
        withTitle={
          <Text isBold style={{wordBreak: 'break-word'}}>
            {(result as WorkstreamBasic).name}
          </Text>
        }
      />
    );

  if (!isEditing) {
    return (
      <Box
        role="button"
        tabIndex={0}
        display="flex"
        alignItems="center"
        borderRadius={bleed ? undefined : 'Medium'}
        paddingY="4"
        onClick={startEditing}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            startEditing();
          }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={shade.className}
        style={{
          ...shade.style,
          gap: 8,
          cursor: 'pointer',
          width: '100%',
          minHeight: rowHeight,
          paddingLeft: 8 + (bleed?.left ?? 0),
          paddingRight: 8 + (bleed?.right ?? 0),
          marginLeft: bleed ? -bleed.left : undefined,
          marginRight: bleed ? -bleed.right : undefined,
        }}
      >
        <UIIcon src={SearchLine} size="small" />
        <Text size="small" color="subtle">
          Find people or workstreams
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" style={{width: '100%', minHeight: rowHeight}}>
      <Typeahead.Wrapper
        onSelection={(value: string) => handleSelection(value)}
        style={{width: '100%'}}
      >
        {({getTriggerProps, getContentProps}) => (
          <>
            <TextInput.Container
              ref={containerRef}
              size={touch ? 'large' : 'small'}
              style={{width: '100%'}}
            >
              <TextInput.Accessory>
                <UIIcon src={SearchLine} size="small" />
              </TextInput.Accessory>
              <TextInput.Input
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Find people or workstreams"
                aria-label="Find people or workstreams"
                value={inputValue}
                {...getTriggerProps({
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setInputValue(event.currentTarget.value),
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      stopEditing();
                    }
                  },
                  // An empty field goes back to the row on leaving; a typed one
                  // keeps its query until a pick or Esc.
                  onBlur: () => {
                    if (!inputValue) stopEditing();
                  },
                })}
              />
            </TextInput.Container>
            <Typeahead.Container
              {...getContentProps()}
              triggerRef={containerRef}
              style={{minWidth: 280}}
            >
              {filtered.length > 0 ? (
                <Typeahead.Results
                  results={filtered}
                  renderRow={renderRow}
                  initialResults={filtered.length}
                  maxResults={filtered.length}
                />
              ) : inputValue.trim() && !isFetchingWorkstreams && !isFetchingPeople ? (
                <Typeahead.Prompt>
                  <Text size="small" color="subtle">
                    No results
                  </Text>
                </Typeahead.Prompt>
              ) : null}
            </Typeahead.Container>
          </>
        )}
      </Typeahead.Wrapper>
    </Box>
  );
};
